import { useState, useEffect, useCallback, useMemo } from "react";
import { authFetch } from "@/lib/authFetch";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// FIN-5 — useFinanceCategorias.
//
// Agregador de Categorias tab:
//   GET /api/categories                              (lista + counts)
//   GET /api/finance/categories/breakdown?month&...  (breakdown + delta)
//   GET /api/accounts                                (pra dropdown filtro)
//
// CRUD: createCategory / updateCategory / deleteCategory (com force).
//
// Filtros: month (YYYY-MM string) + accountIds[] — refazem breakdown.

function _currentMonthIso() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

async function _safeJson(url) {
  try {
    const r = await authFetch(url);
    if (!r.ok) {
      const detail = await r.json().catch(() => ({}));
      return { error: detail?.detail || `HTTP ${r.status}`, data: null };
    }
    return { error: null, data: await r.json() };
  } catch (e) {
    return { error: String(e.message || e), data: null };
  }
}

export default function useFinanceCategorias() {
  const [categories, setCategories] = useState([]);
  const [breakdown, setBreakdown] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [month, setMonth] = useState(_currentMonthIso());
  const [accountIds, setAccountIds] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCategories = useCallback(async () => {
    const r = await _safeJson(`${API_URL}/api/categories`);
    if (r.data) setCategories(Array.isArray(r.data?.categories) ? r.data.categories : []);
    if (r.error) setError(r.error);
  }, []);

  const fetchBreakdown = useCallback(async () => {
    const qp = new URLSearchParams();
    if (month) qp.set("month", month);
    (accountIds || []).forEach(a => qp.append("account_ids", a));
    const r = await _safeJson(`${API_URL}/api/finance/categories/breakdown?${qp.toString()}`);
    if (r.data) setBreakdown(r.data);
    if (r.error) setError(r.error);
  }, [month, accountIds]);

  const fetchAccounts = useCallback(async () => {
    const r = await _safeJson(`${API_URL}/api/accounts`);
    if (r.data) setAccounts(Array.isArray(r.data?.accounts) ? r.data.accounts : []);
  }, []);

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    await Promise.all([fetchCategories(), fetchBreakdown(), fetchAccounts()]);
    setIsLoading(false);
  }, [fetchCategories, fetchBreakdown, fetchAccounts]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // CRUD
  const createCategory = useCallback(async (payload) => {
    const r = await authFetch(`${API_URL}/api/categories`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!r.ok) {
      const detail = await r.json().catch(() => ({}));
      throw new Error(typeof detail?.detail === "string" ? detail.detail : `HTTP ${r.status}`);
    }
    const fresh = await r.json();
    await fetchAll();
    return fresh;
  }, [fetchAll]);

  const updateCategory = useCallback(async (id, patch) => {
    const r = await authFetch(`${API_URL}/api/categories/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    });
    if (!r.ok) {
      const detail = await r.json().catch(() => ({}));
      throw new Error(typeof detail?.detail === "string" ? detail.detail : `HTTP ${r.status}`);
    }
    const fresh = await r.json();
    setCategories(prev => prev.map(c => (c.id === id ? { ...c, ...fresh } : c)));
    fetchAll();
    return fresh;
  }, [fetchAll]);

  const deleteCategory = useCallback(async (id, { force = false } = {}) => {
    const url = `${API_URL}/api/categories/${id}${force ? "?force=true" : ""}`;
    const r = await authFetch(url, { method: "DELETE" });
    if (!r.ok) {
      const body = await r.json().catch(() => ({}));
      // 409 → in_use, retorna estruturado pra UI poder mostrar confirmação
      if (r.status === 409) {
        const err = new Error("in_use");
        err.code = "in_use";
        err.transactionsCount = body?.detail?.transactions_count ?? null;
        err.detail = body?.detail;
        throw err;
      }
      throw new Error(typeof body?.detail === "string" ? body.detail : `HTTP ${r.status}`);
    }
    await fetchAll();
  }, [fetchAll]);

  const knownCategoryNames = useMemo(
    () => new Set(categories.map(c => c.name.toLowerCase())),
    [categories],
  );

  return {
    categories,
    breakdown,
    accounts,
    month,
    accountIds,
    setMonth,
    setAccountIds,
    isLoading,
    error,
    refetch: fetchAll,
    createCategory,
    updateCategory,
    deleteCategory,
    knownCategoryNames,
  };
}
